import { useMutation } from '@apollo/client/react';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { CREATE_BROADCAST_SOURCE_OVERRIDE } from '@optools/services/operator-gateway/btc/mutations';
import { GET_BROADCAST_SOURCE_OVERRIDES } from '@optools/services/operator-gateway/btc/queries';
import {
  BroadcastSourceOverrideInput,
  BroadcastSourceOverridesQuery,
  ProgramSource,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  BroadcastSourceOverrideForm,
  BroadcastSourceOverrideFormValues,
} from './broadcast-source-override-form';

export function AddBroadcastSourceOverride() {
  const [isOpen, setIsOpen] = useState(false);

  const [createBroadcastSourceOverride, { loading }] = useMutation(
    CREATE_BROADCAST_SOURCE_OVERRIDE,
    {
      onCompleted: (data) => {
        const toastTitle = 'Create Broadcast Source Override';
        if (!data?.createBroadcastSourceOverride) {
          toast.error(toastTitle, {
            description: 'Failed to create new broadcast source override',
            duration: Infinity,
          });
          return;
        }

        toast.success(toastTitle, {
          description: `Created new broadcast source override with id: ${data.createBroadcastSourceOverride.id}`,
        });
        setIsOpen(false);
      },
      update: (cache, { data }) => {
        if (!data?.createBroadcastSourceOverride) return;

        const broadcastSourceOverrides = cache.readQuery<BroadcastSourceOverridesQuery>({
          query: GET_BROADCAST_SOURCE_OVERRIDES,
        });

        if (!broadcastSourceOverrides) return;

        const updatedBroadcastSourceOverrides = [
          data.createBroadcastSourceOverride,
          ...broadcastSourceOverrides.broadcastSourceOverrides,
        ];

        cache.writeQuery<BroadcastSourceOverridesQuery>({
          data: {
            broadcastSourceOverrides: updatedBroadcastSourceOverrides,
          },
          query: GET_BROADCAST_SOURCE_OVERRIDES,
        });
      },
    },
  );

  const onSubmit = (values: BroadcastSourceOverrideFormValues) => {
    const { callSign, gameSource, postgameSource, pregameSource } = values;
    const broadcastSourceOverrideInput: BroadcastSourceOverrideInput = {
      callSign,
      gameSource: gameSource === ProgramSource.UNKNOWN ? null : gameSource,
      postgameSource: postgameSource === ProgramSource.UNKNOWN ? null : postgameSource,
      pregameSource: pregameSource === ProgramSource.UNKNOWN ? null : pregameSource,
    };
    createBroadcastSourceOverride({ variables: { broadcastSourceOverrideInput } });
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <DataTableAddButton>Create Override</DataTableAddButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Broadcast Source Override</DialogTitle>
          <DialogDescription className="sr-only">Add a new broadcast override</DialogDescription>
        </DialogHeader>
        <BroadcastSourceOverrideForm disabled={loading} onCancel={onCancel} onSubmit={onSubmit} />
      </DialogContent>
    </Dialog>
  );
}
